import { Injector, runInInjectionContext } from '@angular/core';
import { LocalizationService } from '@abp/ng.core';
import { Column } from 'angular-slickgrid';
import { FieldDefinition } from './field-definition';
import { GenericFormField } from '../generic-form/model/generic-field';
import { localizeWithFallback } from '../utils/localization-utils';
import { getRequiredFromDependencies, hasRequiredEffects } from '../generic-form/utils/field-dependency-utils';
import { ValidationConverterService } from '../services/validation-converter.service';
import { isEmptyValue } from '../utils/value-utils';
import { getEditorRawValue, normalizeValueForRequired } from './helpers/field-editor-utils';

export class FieldMapperHelper {
  /**
   * Builds SlickGrid columns from FieldDefinition[].
   * Simply calls the factories that FieldBuilder created.
   *
   * @param fields Array of field definitions
   * @param localizationService Service for translations
   * @param injector Angular injector for running in injection context
   * @returns Array of SlickGrid Column definitions
   */
  static buildColumnsFromFields(
    fields: FieldDefinition[],
    localizationService: LocalizationService,
    injector: Injector
  ): Column[] {
    const formatAdditionalColumnName = (
      baseName: string,
      suffixName: string,
      suffixKey: string
    ): string => {
      const currentLang = localizationService.currentLang?.split('-')[0]?.toLowerCase();

      if (suffixKey === 'General::Id' && currentLang === 'es') {
        return `${suffixName} ${baseName}`;
      }

      return `${baseName} ${suffixName}`;
    };

    const buildColumn = (
      key: string,
      name: string,
      columnConfig: FieldDefinition['column'],
      fieldOptions?: GenericFormField,
      foreignKey?: FieldDefinition['foreignKey']
    ): Column => {
      const columnParams = columnConfig.params;
      const column: Column = {
        ...columnConfig,
        id: key,
        field: columnConfig.field ?? key,
        name,
        params: foreignKey
          ? {
              ...columnParams,
              foreignKeyConfig: foreignKey,
            }
          : columnParams,
      };

      if (columnConfig.formatter) {
        column.formatter = columnConfig.formatter;
      }

      if (columnConfig.createEditor) {
        column.editor = columnConfig.createEditor(localizationService, injector);
      }

      if (columnConfig.createFilter) {
        column.filter = columnConfig.createFilter(localizationService, injector);
      }

      if (columnConfig.createAsyncPostRender) {
        column.asyncPostRender = columnConfig.createAsyncPostRender(injector);
      }

      if (fieldOptions?.dependencies?.length && column.editor) {
        const rules = fieldOptions.dependencies;
        if (hasRequiredEffects(rules)) {
          const editorConfig = column.editor as any;
          const baseRequired = !!fieldOptions.required;
          const baseValidator = editorConfig.validator;
          const controlType = fieldOptions.controlType;
          const validationConverter = new ValidationConverterService(localizationService);

          column.params = { ...(column.params ?? {}), forceValidateOnBlur: true };
          editorConfig.required = false;
          const dependencyValidator = (value, args) => {
            const rowValues = args?.item ?? {};
            const required = getRequiredFromDependencies(rules, rowValues, baseRequired);
            const processedValue = normalizeValueForRequired(value, controlType, args);
            const rawValue = getEditorRawValue(args);
            const isEmpty = isEmptyValue(processedValue, rawValue);

            if (required && isEmpty) {
              return {
                valid: false,
                msg: localizationService.instant('LanguageService::DefaultErrorRequired'),
              };
            }

            if (!required && isEmpty) {
              return { valid: true, msg: '' };
            }

            if (baseValidator) {
              return baseValidator(processedValue, args);
            }

            return validationConverter.toSlickGridValidator(
              fieldOptions.validators ?? [],
              required,
              controlType
            )(processedValue, args);
          };

          editorConfig.validator = dependencyValidator;
          // Some SlickGrid editors rely on column.validator instead of editor.validator
          (column as any).validator = dependencyValidator;
        }
      }

      return column;
    };

    return fields.flatMap(field => {
      const localizedName = localizeWithFallback(localizationService, field.label);
      const columns = [
        buildColumn(field.key, localizedName, field.column, field.fieldOptions, field.foreignKey),
      ];
      const additionalColumns = (field.additionalGridColumns ?? []).map(extraColumn => {
        let localizedExtraName: string;

        if (extraColumn.label) {
          localizedExtraName = localizeWithFallback(
            localizationService,
            extraColumn.label
          );
        } else if (extraColumn.labelSuffixKey) {
          localizedExtraName = formatAdditionalColumnName(
            localizedName,
            localizeWithFallback(
              localizationService,
              extraColumn.labelSuffixKey
            ),
            extraColumn.labelSuffixKey
          );
        } else {
          localizedExtraName = localizedName;
        }

        return buildColumn(extraColumn.key, localizedExtraName, extraColumn.column);
      });

      return [...columns, ...additionalColumns];
    });
  }

  /**
   * Maps FieldDefinition[] to GenericFormField[] for forms.
   * Applies data context values to each field.
   *
   * @param fields Array of field definitions
   * @param dataContext Entity data to populate form values
   * @param injector Angular injector (not used but kept for API compatibility)
   * @returns Array of form field options
   */
  static mapFieldsForForm(
    fields: FieldDefinition[],
    dataContext: any,
    injector?: Injector
  ): GenericFormField[] {
    const resolvePath = (obj: any, path: string) =>
      path.split('.').reduce((cur, part) => cur?.[part], obj);

    return fields
      .filter(field => !field.fieldOptions?.readOnly || field.fieldOptions.showReadOnlyInForm)
      .map(field => {
      const formField = { ...field.fieldOptions };

      if (formField.optionsLoader && injector) {
        const loader = formField.optionsLoader;
        formField.optionsLoader = (params?: any) =>
          runInInjectionContext(injector, () => loader(params));
      }

      // Apply data context value
      if (dataContext && dataContext !== '') {
        formField.value = dataContext[field.key] ?? '';
      } else {
        formField.value = '';
      }

      if (
        field.foreignKey &&
        formField.options?.length === 0 &&
        dataContext &&
        dataContext !== ''
      ) {
        const value = dataContext[field.key];
        const displayPath = field.column.params?.displayNestedPath as string | undefined;
        const displayField = field.foreignKey.displayField || 'name';
        let displayValue = displayPath ? resolvePath(dataContext, displayPath) : undefined;

        if (!displayPath && typeof field.key === 'string' && field.key.endsWith('Id')) {
          const relationKey = field.key.slice(0, -2);
          const related = dataContext?.[relationKey];
          if (related && typeof related === 'object') {
            displayValue = resolvePath(related, displayField);
          }
        }

        if (value !== null && value !== undefined && displayValue !== null && displayValue !== undefined) {
          formField.options = [{ key: String(value), value: String(displayValue) }];
        }
      }

      return formField;
    });
  }

  // dependency and normalization helpers moved to shared utils
}
